import { lazy, Suspense, useState } from 'react';
import type { ReactNode } from 'react';

import { PostHogErrorBoundary } from 'posthog-js/react';
import {
  createBrowserRouter,
  createMemoryRouter,
  createRoutesFromElements,
  Outlet,
  Route,
  useLocation,
  useNavigate
} from 'react-router';
import { Box } from '@/shared/ui';

import { useEvent } from '@/services/events/react';

import CookieBanner from '@/app/components/CookieBanner';
import GlobalModal from '@/app/components/GlobalModal';
import { AppLayout } from '@/app/layouts/AppLayout';
import { AuthLayout } from '@/app/layouts/AuthLayout';
import { PublicLayout } from '@/app/layouts/PublicLayout';
import { FWDCURVE_CATALOG_PATH, SPOT_CATALOG_PATH } from '@/config/constants';
import { TokenAuth } from '@/features/auth';
import { CatalogSkeleton } from '@/features/catalog';
import { PersonalizationVariantRenderer } from '@/features/component-variants';
import {
  SHARED_DASHBOARD_EXPIRED_PATH,
  SHARED_DASHBOARD_NOT_FOUND_PATH,
  SHARED_DASHBOARD_PATH
} from '@/features/dashboard/constants/const';
import { SharedDashboardExpired } from '@/features/dashboard/pages/SharedDashboard/SharedDashboardExpired';
import { SharedDashboardNotFound } from '@/features/dashboard/pages/SharedDashboard/SharedDashboardNotFound';
import { WidgetAnimationProvider } from '@/features/dashboard/state/WidgetAnimation/provider';
import { FACTSHEET_PARAM_NAME } from '@/features/factsheet';
import { Listeners } from '@/features/listeners';
import MarketsSkeleton from '@/features/markets/components/MarketsSkeleton';
import { MarketsDefaultRedirect } from '@/features/markets/pages/MarketsDefaultRedirect';
import { IdentifyPostHogProvider, TrackingLister } from '@/features/tracking';
import { FeatureFlag } from '@/services/feature-flags';
import { useFlag } from '@/services/feature-flags/react';
import Loader from '@/shared/ui/feedback/Spinner';

const defaultFallback = (
  <Box sx={{ height: '100dvh' }}>
    <Loader />
  </Box>
);

const SuspenseWrapper = ({
  children,
  customKey,
  fallback
}: {
  children: ReactNode;
  customKey?: string;
  fallback?: ReactNode;
}) => {
  const { pathname } = useLocation();
  return (
    <Suspense
      key={customKey ?? pathname}
      fallback={fallback ?? defaultFallback}
    >
      {children}
    </Suspense>
  );
};

const withSuspense = (element: ReactNode, customKey?: string, fallback?: ReactNode) => (
  <SuspenseWrapper
    customKey={customKey}
    fallback={fallback}
  >
    {element}
  </SuspenseWrapper>
);

// Lazy loaded components
const MaintenanceMode = lazy(() =>
  import('@/features/maintenance-mode').then(module => ({ default: module.MaintenanceMode }))
);

const Catalog = lazy(() =>
  import('@/features/catalog').then(module => ({ default: module.CatalogPage }))
);
const ErrorPage = lazy(() => import('@/app/ErrorPage'));
const Expired = lazy(() => import('@/app/Expired'));
const Home = lazy(() => import('@/app/Home'));
const Logout = lazy(() => import('@/app/Logout'));
const Lookup = lazy(() => import('@/features/factsheet/pages/Lookup'));
const Markets = lazy(() => import('@/features/markets/pages/MarketsPage'));
const Login = lazy(() => import('@/features/onboarding/pages/LoginView'));
const OnboardingForm = lazy(() => import('@/features/onboarding/pages/OnboardingFormView'));
const Profile = lazy(() => import('@/features/profile'));
const ManageSubscription = lazy(() => import('@/features/subscription'));
const SubscriptionSuccess = lazy(
  () => import('@/features/subscription/components/SubscriptionSuccess')
);
const SharedDashboard = lazy(() => import('@/features/dashboard/pages/SharedDashboard'));
const FileDownload = lazy(() => import('@/app/FileDownload'));
const Onboarding = lazy(() => import('@/features/onboarding/pages/OnboardingView'));
const Welcome = lazy(() => import('@/app/Welcome'));

const ReCaptchaLayout = lazy(() => import('@/app/layouts/ReCaptchaLayout'));

const MaintenanceGate = () => {
  const isMaintenanceMode = useFlag(FeatureFlag.MaintenanceModeEnabled);
  if (isMaintenanceMode) {
    return <MaintenanceMode />;
  }

  return <Outlet />; // normal app when not in maintenance mode
};

const Wrapper = () => {
  const navigate = useNavigate();
  const [toggleErrorBoundary, setToggleErrorBoundary] = useState(false);

  useEvent('user:logout', ({ reason }) => {
    navigate(reason === 'expired' ? '/expired' : '/logout');
  });

  useEvent('navigation:notfound', () => {
    navigate('/notfound');
  });

  const content = (
    <>
      <Listeners />
      <GlobalModal />
      <CookieBanner />
      <WidgetAnimationProvider>
        <TrackingLister>
          <MaintenanceGate />
        </TrackingLister>
      </WidgetAnimationProvider>
    </>
  );

  return (
    <IdentifyPostHogProvider>
      {import.meta.env.DEV ? (
        content
      ) : (
        <PostHogErrorBoundary
          key={toggleErrorBoundary.toString()} // Used to force remount when toggleErrorBoundary changes
          fallback={<ErrorPage toggleErrorBoundary={setToggleErrorBoundary} />}
        >
          {content}
        </PostHogErrorBoundary>
      )}
    </IdentifyPostHogProvider>
  );
};

const createAppRoutes = () =>
  createRoutesFromElements(
    <Route element={<Wrapper />}>
      <Route element={<ReCaptchaLayout />}>
        <Route
          element={withSuspense(<Onboarding />)}
          path='/onboarding'
        />
        <Route
          element={withSuspense(<Login />)}
          path='/login'
        />
        <Route
          element={withSuspense(<OnboardingForm />)}
          path='/onboarding/form'
        />
      </Route>
      <Route
        element={withSuspense(<Logout />)}
        path='/logout'
      />
      <Route
        element={withSuspense(<Expired />)}
        path='/expired'
      />

      <Route element={<PublicLayout />}>
        <Route
          index
          element={withSuspense(<Home />)}
        />
        <Route
          element={<TokenAuth />}
          path='/token'
        />
        <Route
          element={withSuspense(<Lookup />)}
          path={`/:${FACTSHEET_PARAM_NAME}`}
        />
        <Route element={<AuthLayout />}>
          <Route
            element={withSuspense(<Welcome />)}
            path='/welcome'
          />
          <Route
            element={withSuspense(<FileDownload />)}
            path='/file/:fileName/:fileId'
          />
          <Route element={<AppLayout />}>
            <Route path='markets'>
              <Route
                index
                element={<MarketsDefaultRedirect />}
              />
              <Route
                element={withSuspense(<Markets />, 'markets', <MarketsSkeleton />)}
                path=':marketId/:tabId?'
              />
            </Route>
            <Route
              element={withSuspense(<Catalog />, undefined, <CatalogSkeleton />)}
              path={`/catalog/${SPOT_CATALOG_PATH}`}
            />
            <Route
              element={withSuspense(<Catalog />, undefined, <CatalogSkeleton />)}
              path={`/catalog/${FWDCURVE_CATALOG_PATH}`}
            />
            <Route
              element={withSuspense(<Profile />)}
              path='/profile'
            />
            <Route
              element={withSuspense(<ManageSubscription />)}
              path='/manage-subscription'
            />
            <Route
              element={withSuspense(<SharedDashboard />, undefined, <MarketsSkeleton />)}
              path={SHARED_DASHBOARD_PATH}
            />
            <Route
              element={<PersonalizationVariantRenderer componentName='dashboard_route_element' />}
              path='/dashboard/:dashboardId?/:tabName?'
            />
          </Route>
          <Route
            element={withSuspense(<SubscriptionSuccess />)}
            path='/subscription/success'
          />
        </Route>
      </Route>
      <Route
        element={<SharedDashboardNotFound />}
        path={SHARED_DASHBOARD_NOT_FOUND_PATH}
      />
      <Route
        element={<SharedDashboardExpired />}
        path={SHARED_DASHBOARD_EXPIRED_PATH}
      />
      <Route
        element={withSuspense(<ErrorPage />)}
        path='/notfound'
      />
      <Route
        element={withSuspense(<ErrorPage />)}
        path='/:section/*'
      />
      <Route
        element={withSuspense(<ErrorPage />)}
        path='*'
      />
    </Route>
  );

export const createAppMemoryRouter = (options?: Parameters<typeof createMemoryRouter>[1]) =>
  createMemoryRouter(createAppRoutes(), options);

export const router = createBrowserRouter(createAppRoutes());
